import {
  IconBolt,
  IconBuildingFactory,
  IconCar,
  IconCpu,
  IconFlask,
  IconLeaf,
  IconMedicalCross,
  IconRobot,
  IconShirt,
  type TablerIcon,
} from "@tabler/icons-react";
import {
  operatingIndustryFromUrlSlug,
  operatingIndustryToUrlSlug,
} from "@shared/operatingIndustryUrlSlug";
import {
  EXPLORE_CORRIDORS_TAB,
  EXPLORE_EXPERTS_TAB,
} from "@/pages/discover/discoverExplore.const";

export type EcosystemIndustryVisual = {
  icon: TablerIcon;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
};

const DEFAULT_VISUAL: EcosystemIndustryVisual = {
  icon: IconBuildingFactory,
  iconColor: "#1D4ED8",
  iconBg: "#EFF6FF",
  title: "Ecosystem",
  description: "Connect with verified Experts and grow your business network.",
};

const INDUSTRY_VISUALS: Record<string, EcosystemIndustryVisual> = {
  AUTOMOTIVE_AND_EV: {
    icon: IconCar,
    iconColor: "#2563EB",
    iconBg: "#EFF6FF",
    title: "Automotive Ecosystem",
    description: "Connect with automotive manufacturers, EV innovators and mobility partners.",
  },
  MANUFACTURING_AND_INDUSTRIAL_AUTOMATION: {
    icon: IconRobot,
    iconColor: "#7C3AED",
    iconBg: "#F5F3FF",
    title: "Industrial Automation & Manufacturing",
    description: "Explore robotics, smart factories and industrial automation leaders.",
  },
  HEALTHCARE_AND_LIFE_SCIENCES: {
    icon: IconMedicalCross,
    iconColor: "#16A34A",
    iconBg: "#F0FDF4",
    title: "Healthcare Ecosystem",
    description: "Join healthcare networks, med-tech innovators and life sciences partners.",
  },
  TEXTILES_APPAREL_AND_TECHNICAL_FABRICS: {
    icon: IconShirt,
    iconColor: "#CA8A04",
    iconBg: "#FEFCE8",
    title: "Textile, Apparel & Fashion Ecosystem",
    description: "Connect with textile mills, apparel brands and fashion supply chains.",
  },
  POWER_AND_ENERGY: {
    icon: IconBolt,
    iconColor: "#16A34A",
    iconBg: "#F0FDF4",
    title: "Renewable Energy, Electrification & BESS",
    description: "Discover clean energy, power systems and electrification partners.",
  },
  CLIMATE_TECH_AND_SUSTAINABILITY: {
    icon: IconLeaf,
    iconColor: "#059669",
    iconBg: "#ECFDF5",
    title: "Climate Tech & Sustainability",
    description: "Partner with climate-tech innovators and sustainability-focused businesses.",
  },
  CHEMICALS_AND_SPECIALTY_MATERIALS: {
    icon: IconFlask,
    iconColor: "#7C3AED",
    iconBg: "#F5F3FF",
    title: "Chemicals, Materials & Advanced Manufacturing",
    description: "Explore specialty chemicals, advanced materials and process industries.",
  },
  ICT_AND_DIGITAL_TECH: {
    icon: IconCpu,
    iconColor: "#0EA5E9",
    iconBg: "#F0F9FF",
    title: "Software & Digital Solutions",
    description: "Connect with digital technology, software and ICT partnership ecosystems.",
  },
};

export function getEcosystemIndustryVisual(industry: string, fallbackLabel: string): EcosystemIndustryVisual {
  const visual = INDUSTRY_VISUALS[industry];
  if (visual) return visual;
  return {
    ...DEFAULT_VISUAL,
    title: fallbackLabel.endsWith("Ecosystem") ? fallbackLabel : `${fallbackLabel} Ecosystem`,
  };
}

/** @deprecated Use getEcosystemIndustryVisual */
export function getEcosystemIndustryIcon(industry: string): TablerIcon {
  return getEcosystemIndustryVisual(industry, "Ecosystem").icon;
}

/** @deprecated Use getEcosystemIndustryVisual */
export const ECOSYSTEM_INDUSTRY_DESCRIPTIONS: Record<string, string> = Object.fromEntries(
  Object.entries(INDUSTRY_VISUALS).map(([key, v]) => [key, v.description]),
);

export function industryRouteParam(industry: string): string {
  return operatingIndustryToUrlSlug(industry);
}

export function industryFromRouteParam(param: string): string {
  return operatingIndustryFromUrlSlug(param);
}

/** Passed via react-router location.state when entering head flows from the sector dashboard. */
export type ExpertNavState = {
  returnTo?: string;
};

export function readExpertReturnTo(state: unknown): string | null {
  if (!state || typeof state !== "object") return null;
  const returnTo = (state as ExpertNavState).returnTo;
  return typeof returnTo === "string" && returnTo.trim() ? returnTo : null;
}

export function expertNavState(returnTo?: string): ExpertNavState | undefined {
  const trimmed = returnTo?.trim();
  return trimmed ? { returnTo: trimmed } : undefined;
}

export function sectorHeadsHref(sectorSlug: string): string {
  return `/experts/sector/${encodeURIComponent(sectorSlug)}`;
}

/** @deprecated Use EXPLORE_CORRIDORS_TAB */
export const EXPERTS_AND_CORRIDORS_PATH = EXPLORE_CORRIDORS_TAB;

/** @deprecated Use EXPLORE_EXPERTS_TAB */
export const EXPERTS_AND_CORRIDORS_EXPERTS_TAB = EXPLORE_EXPERTS_TAB;

export { EXPLORE_CORRIDORS_TAB, EXPLORE_EXPERTS_TAB };

export const BOOK_APPOINTMENT_SECTION_ID = "book-appointment";

export function expertProfileHref(industry: string, expertUserId: string): string {
  return `/experts/${industryRouteParam(industry)}/${encodeURIComponent(expertUserId)}`;
}

export function expertCompanyProfileHref(companyId: string): string {
  return `/experts/company/${encodeURIComponent(companyId)}/profile`;
}

export type ExpertCompanyProfileNav = {
  pathname: string;
  state?: ExpertNavState & { expertUserId?: string };
};

export function expertCompanyProfileNav(
  companyId: string,
  expertUserId: string,
  returnTo?: string,
): ExpertCompanyProfileNav {
  return {
    pathname: expertCompanyProfileHref(companyId),
    state: { expertUserId, ...expertNavState(returnTo) },
  };
}

export function expertCompanyProfileNavFromExpert(
  expert:
    | {
      id: string;
      company?: { id: string } | null;
      companyId?: string | null;
    }
    | null
    | undefined,
  returnTo?: string,
): ExpertCompanyProfileNav {
  const companyId = expert?.company?.id ?? expert?.companyId ?? null;
  if (companyId && expert?.id) {
    return expertCompanyProfileNav(companyId, expert.id, returnTo);
  }
  return { pathname: EXPERTS_AND_CORRIDORS_EXPERTS_TAB };
}

/** @deprecated Prefer expertCompanyProfileNavFromExpert for client expert views. */
export function expertProfileHrefFromExpert(expert: {
  id: string;
  ecosystemIndustry: string | null;
} | null | undefined): string {
  return expertCompanyProfileNavFromExpert(expert).pathname;
}

export function navigateToExpertProfile(
  navigate: ReturnType<typeof import("react-router-dom").useNavigate>,
  industry: string,
  expertUserId: string,
  returnTo?: string,
) {
  navigate(expertProfileHref(industry, expertUserId), {
    state: expertNavState(returnTo),
  });
}
